import { Header, Sidebar } from '@/components/layout';
import { Button, Stack, Tabs, TabsContent, TabsList, TabsTrigger, Text } from '@careflux/primitives';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { AudioQualityTab } from './AudioQualityTab';
import { EditRateTab } from './EditRateTab';
import { LatencyTab } from './LatencyTab';
import { RegenerationTab } from './RegenerationTab';
import { SummaryComparisons } from './SummaryComparisons';
import { SummaryQualityFilterBar } from './SummaryQualityFilterBar';
import { VerificationTab } from './VerificationTab';
import type { SummaryQualityTab } from './types';
import { useFilterOptions } from './useFilterOptions';
import { useSummaryQualityFilters } from './useSummaryQualityFilters';
import { SUMMARY_QUALITY_ANALYTICS_KEY, useDebouncedFilters } from './useSummaryQualityQueries';

const TAB_LABELS: Record<SummaryQualityTab, string> = {
  'edit-rate': 'Breytingar',
  latency: 'Biðtími',
  regeneration: 'Endurmyndun',
  verification: 'Sannprófun',
  audio: 'Hljóðgæði',
};

export const SummaryQualityPage = () => {
  const { filters, tab, bucket, hasActiveFilters, setFilter, setTab, setBucket, clearFilters } =
    useSummaryQualityFilters();
  const debouncedFilters = useDebouncedFilters(filters);
  const { options, isLoading: optionsLoading } = useFilterOptions(debouncedFilters, tab !== 'audio');
  const tabProps = { filters: debouncedFilters, bucket, onBucketChange: setBucket };
  const queryClient = useQueryClient();
  const handleRefresh = () => void queryClient.invalidateQueries({ queryKey: [SUMMARY_QUALITY_ANALYTICS_KEY] });

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <Header title="Gæðagreining">
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw />
            Uppfæra
          </Button>
        </Header>
        <main className="flex-1 overflow-y-auto p-8">
          <Stack gap="xl" className="mx-auto max-w-7xl">
            <Stack gap="xs">
              <Text asChild size="3xl" weight="semibold">
                <h1>Gæðagreining</h1>
              </Text>
              <Text size="sm" color="muted">
                Breytingar á AI samantektum, biðtími, endurmyndanir, sannprófun og áætluð hljóðgæði.
              </Text>
            </Stack>

            {tab !== 'audio' ? (
              <SummaryQualityFilterBar
                filters={filters}
                options={options}
                optionsLoading={optionsLoading}
                hasActiveFilters={hasActiveFilters}
                onChange={setFilter}
                onClear={clearFilters}
              />
            ) : null}

            <Tabs value={tab} onValueChange={(value) => setTab(value as SummaryQualityTab)}>
              <TabsList>
                {(Object.keys(TAB_LABELS) as SummaryQualityTab[]).map((value) => (
                  <TabsTrigger key={value} value={value}>
                    {TAB_LABELS[value]}
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="edit-rate">
                <EditRateTab {...tabProps} />
              </TabsContent>
              <TabsContent value="latency">
                <LatencyTab {...tabProps} />
              </TabsContent>
              <TabsContent value="regeneration">
                <RegenerationTab {...tabProps} />
              </TabsContent>
              <TabsContent value="verification">
                <VerificationTab filters={debouncedFilters} />
              </TabsContent>
              <TabsContent value="audio">
                <AudioQualityTab
                  filters={filters}
                  bucket={bucket}
                  onBucketChange={setBucket}
                  onFilterChange={setFilter}
                />
              </TabsContent>
            </Tabs>

            {tab !== 'audio' ? <SummaryComparisons dateRange={debouncedFilters} /> : null}
          </Stack>
        </main>
      </div>
    </div>
  );
};
